import { useEffect, useState } from 'react';

/**
 * Firestore collection holding one-off UI flags, keyed `user_{userId}` exactly like the
 * notification collections (`cookbook_notifications`, `tracker_notifications`, …).
 */
const UI_FLAGS_COLLECTION = 'ui_flags';

/**
 * The scan-label intro's key inside that document. Numeric because the shared helpers type their
 * key as `notificationId: number` — the flags live alongside notification ids in the same shape.
 */
export const SCAN_LABEL_INTRO_FLAG = 1;

/**
 * Has the user already seen the scan-label intro ("Take 2 photos")?
 *
 * Stored in Firestore, NOT in localStorage. The Android WebView drops DOM storage when the shell
 * tears it down, so a localStorage flag came back unset on every app launch and the intro reopened
 * each time — while iOS (WKWebView), which persists it, behaved correctly (IP-11131). Firestore is
 * where the app already keeps this kind of per-user, once-only UI state.
 *
 * Returns `null` while the answer is still unknown, so the caller can hold the sheet back rather
 * than flash it and take it away once Firestore replies.
 */
export const useScanLabelIntroSeen = (
  userId: number,
  getClosedNotifications?: (collectionName: string, userId: number) => Promise<number[]>
) => {
  const [hasSeenIntro, setHasSeenIntro] = useState<boolean | null>(null);

  useEffect(() => {
    let active = true;

    // No Firestore helper wired in (brands pass it from their own Firebase app) or nobody to key
    // the document on: treat the intro as unseen so the screen still explains itself. It just
    // cannot be remembered, which is the pre-existing behaviour for those cases.
    if (!getClosedNotifications || !userId) {
      setHasSeenIntro(false);
      return undefined;
    }

    getClosedNotifications(UI_FLAGS_COLLECTION, userId)
      .then((flags) => {
        if (active) setHasSeenIntro(flags.includes(SCAN_LABEL_INTRO_FLAG));
      })
      // A failed read must not hide the intro from someone who has never seen it.
      .catch(() => {
        if (active) setHasSeenIntro(false);
      });

    return () => {
      active = false;
    };
  }, [getClosedNotifications, userId]);

  return { hasSeenIntro, setHasSeenIntro };
};

export { UI_FLAGS_COLLECTION };
